import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Asociado {
  id: string;
  asociado_id?: string;
  nombre: string;
  correo?: string;
  telefono?: string;
  calificacion?: number;
  inmobiliaria_doc_id?: string;
  años_experiencia?: number;
  estatus?: string;
  propsActivas?: number;
  inmobiliaria?: string;
  [key: string]: any;
}

export default function AdminAsociados() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [asociados, setAsociados] = useState<Asociado[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroInmobiliaria, setFiltroInmobiliaria] = useState('');
  const [loading, setLoading] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const asociadosPorPagina = 10;

  useEffect(() => {
    if (!user) return;

    const fetchAsociados = async () => {
      if (!Array.isArray(user.tipo) || !user.tipo.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para ver esta sección.'
        });
        return navigate('/');
      }

      try {
        console.log('📊 Cargando asociados...');

        // ✅ Cargar todos los asociados (filtraremos activos después)
        const asociadosSnap = await getDocs(collection(db, 'asociado'));

        // ✅ Filtrar solo activos o sin estatus (por defecto activos)
        const asociadosDocs = asociadosSnap.docs.filter(docSnap => {
          const data = docSnap.data();
          return !data.estatus || data.estatus === 'activo';
        });

        console.log(`✅ Asociados activos encontrados: ${asociadosDocs.length}`);

        const lista = await Promise.all(
          asociadosDocs.map(async (docSnap) => {
            const data = docSnap.data();

            // ✅ Contar propiedades usando inmobiliaria_doc_id
            let propsActivas = 0;
            if (data.inmobiliaria_doc_id) {
              try {
                const propsSnap = await getDocs(collection(db, 'propiedades'));
                propsActivas = propsSnap.docs.filter(propDoc => {
                  const propData = propDoc.data();
                  return propData.inmobiliaria_doc_id === data.inmobiliaria_doc_id && propData.activo === true;
                }).length;
              } catch (error) {
                console.error(`Error contando propiedades para ${data.nombre}:`, error);
              }
            }

            // ✅ Buscar inmobiliaria usando inmobiliaria_doc_id
            let inmobiliaria = 'Sin inmobiliaria';
            if (data.inmobiliaria_doc_id) {
              try {
                const inmoDocRef = doc(db, 'inmobiliaria', data.inmobiliaria_doc_id);
                const inmoSnap = await getDoc(inmoDocRef);

                if (inmoSnap.exists()) {
                  inmobiliaria = inmoSnap.data().nombre || 'Sin nombre';
                } else {
                  console.warn(`⚠️ Inmobiliaria no encontrada: ${data.inmobiliaria_doc_id}`);
                }
              } catch (error) {
                console.error(`Error buscando inmobiliaria para ${data.nombre}:`, error);
              }
            }

            return {
              id: docSnap.id,
              nombre: data.nombre,
              correo: data.correo,
              telefono: data.telefono,
              calificacion: data.calificacion,
              inmobiliaria_doc_id: data.inmobiliaria_doc_id,
              años_experiencia: data.años_experiencia,
              estatus: data.estatus,
              propsActivas,
              inmobiliaria,
              ...data
            } as Asociado;
          })
        );

        // ✅ Ordenar alfabéticamente por nombre
        lista.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          return nombreA.localeCompare(nombreB);
        });

        console.log(`✅ Lista de asociados procesada: ${lista.length}`);
        setAsociados(lista);
      } catch (err) {
        console.error('❌ Error al cargar asociados:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la lista de asociados.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAsociados();
  }, [user, navigate]);

  // ✅ Filtros aplicados
  const asociadosFiltrados = asociados.filter(a => {
    const matchNombre = a.nombre?.toLowerCase().includes(filtro.toLowerCase());
    const matchCorreo = a.correo?.toLowerCase().includes(filtro.toLowerCase());
    const matchInmobiliaria = filtroInmobiliaria === '' || a.inmobiliaria?.toLowerCase().includes(filtroInmobiliaria.toLowerCase());

    return (matchNombre || matchCorreo) && matchInmobiliaria;
  });

  // ✅ Paginación
  const totalPaginas = Math.ceil(asociadosFiltrados.length / asociadosPorPagina);
  const startIndex = (paginaActual - 1) * asociadosPorPagina;
  const asociadosPaginados = asociadosFiltrados.slice(startIndex, startIndex + asociadosPorPagina);

  // ✅ Obtener inmobiliarias únicas para filtro
  const inmobiliariasUnicas = Array.from(new Set(
    asociados.map(a => a.inmobiliaria).filter(Boolean)
  )).sort();

  const handleEliminar = async (id: string, nombre: string) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar asociado?',
      html: `¿Estás seguro de eliminar a <strong>${nombre}</strong>?<br>Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'asociado', id));
        setAsociados(prev => prev.filter(a => a.id !== id));

        Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: `El asociado ${nombre} fue eliminado exitosamente.`
        });
      } catch (error) {
        console.error('Error eliminando asociado:', error);
        Swal.fire('Error', 'No se pudo eliminar el asociado. Intenta nuevamente.', 'error');
      }
    }
  };

  if (!user) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Acceso denegado</h4>
          <p>No tienes permiso para ver esta sección.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container mt-5 mb-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Asociados' }
          ]}
        />

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">
            <i className="fas fa-user-friends me-2 text-primary"></i>
            Administración de Asociados
          </h2>
          <button className="btn btn-success" onClick={() => navigate('/add-aso')}>
            <i className="fas fa-plus me-2"></i>
            Agregar Asociado
          </button>
        </div>

        {/* Estadísticas */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center bg-primary text-white shadow-sm">
              <div className="card-body">
                <h3>{asociados.length}</h3>
                <p className="mb-0">Total Asociados</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-success text-white shadow-sm">
              <div className="card-body">
                <h3>{asociados.filter(a => a.propsActivas && a.propsActivas > 0).length}</h3>
                <p className="mb-0">Con Propiedades</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-info text-white shadow-sm">
              <div className="card-body">
                <h3>{asociados.filter(a => a.años_experiencia && a.años_experiencia >= 10).length}</h3>
                <p className="mb-0">Experiencia 10+ años</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-warning text-white shadow-sm">
              <div className="card-body">
                <h3>{asociadosFiltrados.length}</h3>
                <p className="mb-0">Filtrados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label">Buscar por nombre o correo:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar asociados..."
                  value={filtro}
                  onChange={e => {
                    setFiltro(e.target.value);
                    setPaginaActual(1);
                  }}
                />
              </div>
              <div className="col-md-5">
                <label className="form-label">Filtrar por inmobiliaria:</label>
                <select
                  className="form-select"
                  value={filtroInmobiliaria}
                  onChange={e => {
                    setFiltroInmobiliaria(e.target.value);
                    setPaginaActual(1);
                  }}
                >
                  <option value="">Todas las inmobiliarias</option>
                  {inmobiliariasUnicas.map(inmo => (
                    <option key={inmo} value={inmo}>{inmo}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setFiltro('');
                    setFiltroInmobiliaria('');
                    setPaginaActual(1);
                  }}
                >
                  <i className="fas fa-times me-1"></i>Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Cargando asociados...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-hover shadow-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Teléfono</th>
                    <th>Agencia</th>
                    <th>Experiencia</th>
                    <th>Propiedades</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {asociadosPaginados.length > 0 ? (
                    asociadosPaginados.map(aso => (
                      <tr key={aso.id}>
                        <td>
                          <i className="fas fa-user-circle me-2 text-muted"></i>
                          <strong>{aso.nombre}</strong>
                        </td>
                        <td>{aso.correo || <span className="text-muted">No disponible</span>}</td>
                        <td>{aso.telefono || <span className="text-muted">No disponible</span>}</td>
                        <td>
                          <i className="fas fa-building me-1 text-primary"></i>
                          {aso.inmobiliaria || 'Sin asignar'}
                        </td>
                        <td className="text-center">
                          {aso.años_experiencia ? (
                            <span className="badge bg-info">
                              <i className="fas fa-briefcase me-1"></i>
                              {aso.años_experiencia} años
                            </span>
                          ) : (
                            <span className="badge bg-secondary">Sin especificar</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${aso.propsActivas && aso.propsActivas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                            {aso.propsActivas || 0}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => navigate(`/ver-admin-asociado/${aso.id}`)}
                              title="Ver detalles"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => navigate(`/editar-asociado/${aso.id}`)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleEliminar(aso.id, aso.nombre)}
                              title="Eliminar"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="mb-0">
                          {filtro || filtroInmobiliaria
                            ? 'No se encontraron asociados con ese criterio de búsqueda.'
                            : 'No hay asociados registrados.'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <nav className="d-flex justify-content-center mt-4" aria-label="Paginación de asociados">
                <ul className="pagination flex-wrap">
                  <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual - 1)}
                      disabled={paginaActual === 1}
                    >
                      <i className="fas fa-chevron-left me-1"></i>Anterior
                    </button>
                  </li>

                  {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                    let pageNum: number;
                    if (totalPaginas <= 5) {
                      pageNum = i + 1;
                    } else if (paginaActual <= 3) {
                      pageNum = i + 1;
                    } else if (paginaActual >= totalPaginas - 2) {
                      pageNum = totalPaginas - 4 + i;
                    } else {
                      pageNum = paginaActual - 2 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                    >
                      Siguiente<i className="fas fa-chevron-right ms-1"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            {/* Información de paginación */}
            <div className="text-center text-muted">
              Mostrando {startIndex + 1} a {Math.min(startIndex + asociadosPorPagina, asociadosFiltrados.length)} de {asociadosFiltrados.length} asociados
            </div>
          </>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
